import { useEffect } from "react";
import { useReveal } from "@/hooks/useReveal";

const VALUES = [
  {
    title: "Direct Accountability",
    desc: "Juan Sahagun personally walks every site — the person who quotes your job is the person who answers for it.",
  },
  {
    title: "One Crew, Start To Finish",
    desc: "Design, build, irrigation, and year-round maintenance from the same team. No hand-offs, no finger-pointing.",
  },
  {
    title: "Fair, Honest Pricing",
    desc: "Clear quotes, no surprise line items, and fast reliable scheduling across Greater Los Angeles.",
  },
];

const AboutPage = () => {
  const ref = useReveal();

  useEffect(() => {
    document.title = "About Us | Sahagun Landscape — Paramount, CA";
    window.scrollTo(0, 0);
    return () => {
      document.title = "Sahagun Landscape | Landscaping & Hardscaping — Paramount, CA";
    };
  }, []);

  return (
    <div ref={ref} data-testid="about-page" className="bg-[#faf8f5] text-[#1f281e]">
      {/* Page hero */}
      <section className="relative py-20 md:py-28 border-b border-[#2d4a22]/10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03] d1-drift pointer-events-none"
          style={{ backgroundImage: "radial-gradient(#2d4a22 1.5px, transparent 1.5px)" }}
        />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <span className="font-mono-type text-xs uppercase tracking-[0.18em] text-[#2d4a22] font-semibold block mb-4">
            About Us
          </span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.95] max-w-3xl">
            <span className="ln"><span>A Family Crew That Treats</span></span>
            <span className="ln"><span className="text-[#2d4a22]">Your Yard Like Our Own.</span></span>
          </h1>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <span className="font-mono-type text-xs uppercase tracking-[0.18em] text-[#2d4a22] font-semibold block mb-2">
              Our Story
            </span>
            <h2 className="font-display text-3xl md:text-4xl uppercase mb-6">Rooted In Paramount</h2>
            <p className="text-base text-[#3d4b3b] leading-relaxed mb-5">
              Sahagun Landscape is a family-owned company based at 15306 Castana Ave in Paramount, serving homes and
              businesses across Los Angeles County and Greater Southern California. We handle everything outdoors —
              landscaping and design, hardscaping, irrigation systems, tree services, artificial turf, sod,
              gardening, and full-service maintenance.
            </p>
            <p className="text-base text-[#3d4b3b] leading-relaxed">
              What makes us different is simple: sales specialist Juan Sahagun personally oversees every job, and one
              dedicated crew carries your project from first sketch to the last seasonal visit. When you call, you
              talk to the people who actually do the work.
            </p>
          </div>
          <div className="lg:col-span-5 reveal reveal-d1">
            <figure className="img-zoom relative aspect-[4/3] bg-[#e4dacb] overflow-hidden border border-[#2d4a22]/20">
              <img
                src="/images/photo_6.jpg"
                alt="Sahagun Landscape crew in green uniforms caring for a commercial property"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <figcaption className="absolute bottom-0 left-0 right-0 p-5 text-[11px] uppercase tracking-widest text-[#faf8f5] bg-gradient-to-t from-[#1f281e]/85 to-transparent">
                Our crew on a commercial grounds job
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-24 bg-[#f2ece1] border-y border-[#2d4a22]/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal mb-12">
            <span className="font-mono-type text-xs uppercase tracking-[0.18em] text-[#2d4a22] font-semibold block mb-2">
              What We Stand For
            </span>
            <h2 className="font-display text-3xl md:text-4xl uppercase">How We Work</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <div key={v.title} className={`reveal reveal-d${i + 1} p-8 bg-[#faf8f5] border border-[#2d4a22]/10`} data-testid={`value-${i + 1}`}>
                <span className="block w-8 h-1 bg-[#2d4a22] organic-grow mb-5" />
                <h3 className="text-lg font-bold uppercase mb-3">{v.title}</h3>
                <p className="text-sm text-[#3d4b3b] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Juan */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 reveal order-2 lg:order-1">
            <figure className="img-zoom relative aspect-[4/3] bg-[#e4dacb] overflow-hidden border border-[#2d4a22]/20">
              <img
                src="/images/photo_7.jpg"
                alt="Hands-on irrigation valve repair by the Sahagun Landscape team"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <figcaption className="absolute bottom-0 left-0 right-0 p-5 text-[11px] uppercase tracking-widest text-[#faf8f5] bg-gradient-to-t from-[#1f281e]/85 to-transparent">
                Hands-on, every single job
              </figcaption>
            </figure>
          </div>
          <div className="lg:col-span-7 reveal reveal-d1 order-1 lg:order-2">
            <span className="font-mono-type text-xs uppercase tracking-[0.18em] text-[#2d4a22] font-semibold block mb-2">
              Meet The Specialist
            </span>
            <h2 className="font-display text-3xl md:text-4xl uppercase mb-6">Juan Sahagun</h2>
            <p className="text-base text-[#3d4b3b] leading-relaxed mb-5">
              Juan leads sales and oversees every project personally — from the first walkthrough and honest quote to
              the final inspection. Residential yard or commercial campus, you get the same standard: done right, on
              schedule, at a fair price.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <a
                href="tel:+13232204640"
                data-testid="about-call-btn"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#2d4a22] text-[#faf8f5] font-semibold text-xs uppercase tracking-[0.16em] hover:bg-[#1a2d14] transition-colors"
              >
                Call +1 (323) 220-4640
              </a>
              <a
                href="/#contact"
                data-testid="about-quote-btn"
                className="inline-flex items-center justify-center px-8 py-4 border border-[#2d4a22] text-[#2d4a22] font-semibold text-xs uppercase tracking-[0.16em] hover:bg-[#2d4a22]/5 transition-colors"
              >
                Request Free Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service area band */}
      <section className="py-16 bg-[#2d4a22] text-[#faf8f5]">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 reveal">
          <div>
            <h2 className="font-display text-2xl md:text-3xl uppercase mb-2">Proudly Serving Greater Los Angeles</h2>
            <p className="text-sm text-[#d3e4d0]">
              Paramount and nearby cities, all of Los Angeles County, and Southern California — residential and commercial.
            </p>
          </div>
          <a
            href="/careers"
            data-testid="about-careers-btn"
            className="inline-flex items-center justify-center px-8 py-4 border border-[#faf8f5]/60 text-[#faf8f5] font-semibold text-xs uppercase tracking-[0.16em] hover:bg-[#faf8f5]/10 transition-colors"
          >
            Work With Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
